import { useRef, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import * as React from "react";
import { Save, ArrowLeft, Plus, X, Search } from "lucide-react";
import { CreateGroupData, UpdateGroupData, groupsApi } from "../lib/groups";
import { usersApi } from "../lib/users";
import { sitesApi } from "../lib/sites";
import { presetsApi, PresetResponse } from "../lib/presets";
import { siteControllersApi } from "../lib/site-controllers";
import { areasApi, AreaResponse } from "../lib/areas";
import { useAuth } from "../hooks/useAuth";
import LightPresetGroupFormModal from "../components/presets/LightPresetGroupFormModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { canManageGroups } from "../utils/role-utils";
import GroupLightingPresetsList from "../components/groups/GroupLightingPresetsList";

export default function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = id !== "new";

  // Get siteId from location state if navigating from SiteDetailPage
  const siteIdFromState = location.state?.siteId as string | undefined;

  const [formData, setFormData] = useState({
    siteId: siteIdFromState || "",
    name: "",
    pin: "",
    userIds: [] as string[],
    presetIds: [] as string[],
    controllerUserPreset: "",
  });

  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  // NOTE: aliases are edited via `LightPresetGroupFormModal`, not on this page.
  const [lpgModal, setLpgModal] = useState<{
    open: boolean;
    lightPresetGroupId: string | null;
    preset: PresetResponse | null;
  }>({ open: false, lightPresetGroupId: null, preset: null });
  const [uiAlert, setUiAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({ open: false, title: "Notice", message: "" });

  const didInitPresetIdsRef = useRef(false);
  const [savedPresetIds, setSavedPresetIds] = useState<string[] | null>(null);
  const [editLightingPresetsOpen, setEditLightingPresetsOpen] = useState(false);
  const [draftPresetIds, setDraftPresetIds] = useState<string[]>([]);
  /** When opening the LPG modal from the "Edit lighting presets" dialog, reopen that dialog after save/cancel. */
  const reopenEditLightingPresetsAfterLpgRef = useRef(false);

  const openLpgModalFromReadonlyList = (args: {
    preset: PresetResponse;
    lightPresetGroupId: string;
  }) => {
    reopenEditLightingPresetsAfterLpgRef.current = false;
    setLpgModal({
      open: true,
      lightPresetGroupId: args.lightPresetGroupId,
      preset: args.preset,
    });
  };

  const openLpgModalFromEditLightingPresets = (args: {
    preset: PresetResponse;
    lightPresetGroupId: string;
  }) => {
    reopenEditLightingPresetsAfterLpgRef.current = true;
    setLpgModal({
      open: true,
      lightPresetGroupId: args.lightPresetGroupId,
      preset: args.preset,
    });
  };

  const closeLpgModal = () => {
    if (reopenEditLightingPresetsAfterLpgRef.current) {
      reopenEditLightingPresetsAfterLpgRef.current = false;
      setEditLightingPresetsOpen(true);
    }
    setLpgModal({ open: false, lightPresetGroupId: null, preset: null });
  };

  // Fetch group data if editing
  const { data: group, isLoading: groupLoading } = useQuery({
    queryKey: ["group", id],
    queryFn: () => groupsApi.getById(id!),
    enabled: isEditing && !!id,
  });

  const { data: sites } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.getAll(),
  });

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: () => usersApi.getAll(),
  });

  // Fetch controllers for the selected site
  const { data: controllers } = useQuery({
    queryKey: ["site-controllers", formData.siteId],
    queryFn: () => siteControllersApi.getBySite(formData.siteId),
    enabled: !!formData.siteId,
  });

  // Fetch areas for all controllers in the site
  const { data: allAreas } = useQuery({
    queryKey: ["all-areas", formData.siteId, controllers?.map((c) => c.id)],
    queryFn: async () => {
      if (!controllers || controllers.length === 0) return [];
      const areaPromises = controllers.map((controller) =>
        areasApi.getBySiteController(controller.id),
      );
      const areaArrays = await Promise.all(areaPromises);
      return areaArrays.flat();
    },
    enabled: !!formData.siteId && !!controllers && controllers.length > 0,
  });

  // Fetch light type areas for presets section
  const { data: lightAreas } = useQuery({
    queryKey: ["light-areas", formData.siteId, controllers?.map((c) => c.id)],
    queryFn: async () => {
      if (!controllers || controllers.length === 0) return [];
      const areaPromises = controllers.map((controller) =>
        areasApi.getBySiteController(controller.id, "light"),
      );
      const areaArrays = await Promise.all(areaPromises);
      return areaArrays.flat();
    },
    enabled: !!formData.siteId && !!controllers && controllers.length > 0,
  });

  // Fetch presets for all areas in the site (for controllerUserPreset dropdown)
  const { data: allPresets } = useQuery({
    queryKey: ["all-presets", formData.siteId, allAreas?.map((a) => a.id)],
    queryFn: async () => {
      if (!allAreas || allAreas.length === 0) return [];
      const presetPromises = allAreas.map((area) =>
        presetsApi.getByArea(area.id),
      );
      const presetArrays = await Promise.all(presetPromises);
      return presetArrays.flat();
    },
    enabled: !!formData.siteId && !!allAreas && allAreas.length > 0,
  });

  // Fetch light type presets for presets section
  const { data: lightPresets } = useQuery({
    queryKey: ["light-presets", formData.siteId, lightAreas?.map((a) => a.id)],
    queryFn: async () => {
      if (!lightAreas || lightAreas.length === 0) return [];
      const presetPromises = lightAreas.map((area) =>
        presetsApi.getByArea(area.id),
      );
      const presetArrays = await Promise.all(presetPromises);
      // Filter to only light type presets
      return presetArrays.flat().filter((preset) => preset.type === "light");
    },
    enabled: !!formData.siteId && !!lightAreas && lightAreas.length > 0,
  });

  // Get the current site object based on formData.siteId
  const currentSite = sites?.find((site) => site.id === formData.siteId);

  // Load group data when editing
  useEffect(() => {
    if (group) {
      // Set siteId and ensure it's from the group's associated site
      const groupSiteId = group.siteId;
      setFormData({
        siteId: groupSiteId,
        name: group.name,
        pin: group.pin,
        userIds: group.members?.map((member) => member.id) ?? [],
        presetIds: [], // Will be populated from preset queries if needed
        controllerUserPreset: group.controllerUserPreset || "",
      });
    } else if (!isEditing) {
      // For new groups, prioritize siteId from state (passed from SiteDetailPage)
      if (siteIdFromState) {
        setFormData((prev) => {
          // Only update if siteId is not already set or different
          if (prev.siteId !== siteIdFromState) {
            return {
              ...prev,
              siteId: siteIdFromState,
            };
          }
          return prev;
        });
      } else if (sites && sites.length > 0) {
        // Fallback to first available site if no siteId from state
        setFormData((prev) => {
          if (!prev.siteId) {
            return {
              ...prev,
              siteId: sites[0].id,
            };
          }
          return prev;
        });
      }
    }
  }, [group, sites, isEditing, siteIdFromState]);

  // When editing and we have presets loaded, find which presets are linked to this group
  // Only check light presets since lightPresetGroups only links light type presets
  useEffect(() => {
    if (didInitPresetIdsRef.current) return;
    if (isEditing && group && lightPresets) {
      // Find presets that have this group in their groups array
      const linkedPresetIds = lightPresets
        .filter((preset) => preset.groups?.some((g) => g.groupId === group.id))
        .map((preset) => preset.id);

      setFormData((prev) => ({
        ...prev,
        presetIds: linkedPresetIds,
      }));
      setSavedPresetIds(linkedPresetIds);
      didInitPresetIdsRef.current = true;
    }
  }, [isEditing, group, lightPresets]);

  useEffect(() => {
    if (!editLightingPresetsOpen) return;
    setDraftPresetIds(formData.presetIds);
  }, [editLightingPresetsOpen, formData.presetIds]);

  // (Aliases are edited via the LPG modal; no alias state needed here.)

  // Group presets by area (only light type)
  const presetsByArea = React.useMemo(() => {
    if (!lightPresets || !lightAreas)
      return new Map<
        string,
        { area: AreaResponse; presets: PresetResponse[] }
      >();

    const grouped = new Map<
      string,
      { area: AreaResponse; presets: PresetResponse[] }
    >();

    lightPresets.forEach((preset) => {
      const area = lightAreas.find((a) => a.id === preset.areaId);
      if (area) {
        if (!grouped.has(area.id)) {
          grouped.set(area.id, { area, presets: [] });
        }
        grouped.get(area.id)!.presets.push(preset);
      }
    });

    return grouped;
  }, [lightPresets, lightAreas]);

  const createMutation = useMutation({
    mutationFn: (data: CreateGroupData) => groupsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groups", formData.siteId] });
      navigate(-1); // Go back to previous page
    },
    onError: (error: unknown) => {
      console.error("Error creating group:", error);
      const errorMessage =
        (
          error as {
            response?: { data?: { error?: string } };
            message?: string;
          }
        )?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Failed to create group";
      setUiAlert({ open: true, title: "Error", message: errorMessage });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGroupData }) =>
      groupsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["group", id] });
      queryClient.invalidateQueries({ queryKey: ["groups", formData.siteId] });
      navigate(-1); // Go back to previous page
    },
    onError: (error: unknown) => {
      console.error("Error updating group:", error);
      const errorMessage =
        (
          error as {
            response?: { data?: { error?: string } };
            message?: string;
          }
        )?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Failed to update group";
      setUiAlert({ open: true, title: "Error", message: errorMessage });
    },
  });

  const savePresetMappingsMutation = useMutation({
    mutationFn: ({ id, presetIds }: { id: string; presetIds: string[] }) => {
      // IMPORTANT: do not send presetAliases here; aliases are edited via the LPG modal.
      const payload: UpdateGroupData = {
        presetIds: presetIds.length > 0 ? presetIds : undefined,
      };
      return groupsApi.update(id, payload);
    },
    onSuccess: () => {
      if (id) {
        queryClient.invalidateQueries({ queryKey: ["group", id] });
      }
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groups", formData.siteId] });
      queryClient.invalidateQueries({
        queryKey: ["light-presets", formData.siteId],
      });
      // setUiAlert({
      //   open: true,
      //   title: "Saved",
      //   message: "Preset mappings updated.",
      // });
    },
    onError: (error: unknown) => {
      const msg =
        (
          error as {
            response?: { data?: { error?: string } };
            message?: string;
          }
        )?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Failed to update preset mappings";
      setUiAlert({ open: true, title: "Error", message: msg });
    },
  });

  const addUserMutation = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      groupsApi.addUser(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group", id] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groups", formData.siteId] });
      setAddUserDialogOpen(false);
      setUserSearchQuery("");
    },
    onError: (error: unknown) => {
      console.error("Error adding user to group:", error);
      const errorMessage =
        (
          error as {
            response?: { data?: { error?: string } };
            message?: string;
          }
        )?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Failed to add user to group";
      setUiAlert({ open: true, title: "Error", message: errorMessage });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      groupsApi.removeUser(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group", id] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groups", formData.siteId] });
    },
    onError: (error: unknown) => {
      console.error("Error removing user from group:", error);
      const errorMessage =
        (
          error as {
            response?: { data?: { error?: string } };
            message?: string;
          }
        )?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Failed to remove user from group";
      setUiAlert({ open: true, title: "Error", message: errorMessage });
    },
  });

  const handleUserToggle = (userId: string) => {
    setFormData((prev) => {
      const isSelected = prev.userIds.includes(userId);
      return {
        ...prev,
        userIds: isSelected
          ? prev.userIds.filter((id) => id !== userId)
          : [...prev.userIds, userId],
      };
    });
  };

  const handlePresetToggle = (presetId: string) => {
    didInitPresetIdsRef.current = true;
    setDraftPresetIds((prev) => {
      const isSelected = prev.includes(presetId);
      const next = isSelected
        ? prev.filter((id) => id !== presetId)
        : [...prev, presetId];
      if (isEditing && id) {
        savePresetMappingsMutation.mutate(
          { id, presetIds: next },
          {
            onSuccess: () => {
              setFormData((p) => ({ ...p, presetIds: next }));
              setSavedPresetIds(next);
            },
          },
        );
      }
      return next;
    });
  };

  const handleAreaToggle = (areaPresets: PresetResponse[]) => {
    didInitPresetIdsRef.current = true;
    setDraftPresetIds((prev) => {
      const areaPresetIds = areaPresets.map((p) => p.id);
      const allSelected = areaPresetIds.every((id) => prev.includes(id));
      const next = allSelected
        ? prev.filter((id) => !areaPresetIds.includes(id))
        : [...prev, ...areaPresetIds.filter((id) => !prev.includes(id))];
      if (isEditing && id) {
        savePresetMappingsMutation.mutate(
          { id, presetIds: next },
          {
            onSuccess: () => {
              setFormData((p) => ({ ...p, presetIds: next }));
              setSavedPresetIds(next);
            },
          },
        );
      }
      return next;
    });
  };

  const hasPermission = canManageGroups(currentUser?.role);

  // Only end users can be members of groups
  const endUsers = users?.filter((user) => user.role === "end_user") || [];

  // Filter users to only show users from the selected site
  const siteUsers = formData.siteId
    ? endUsers.filter((user) =>
        user.sites?.some((site) => site.id === formData.siteId),
      )
    : endUsers;

  // Filter users for the add user dialog (exclude already added users)
  const availableUsersForAdd = endUsers.filter(
    (user) =>
      !formData.userIds.includes(user.id) &&
      (!formData.siteId ||
        user.sites?.some((site) => site.id === formData.siteId)) &&
      (userSearchQuery === "" ||
        user.email.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        user.firstName?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(userSearchQuery.toLowerCase())),
  );

  // Check if this is a Manager group
  const isManagerGroup =
    group?.name === "Manager" || formData.name === "Manager";

  // Get managers (super_users and site_admins) from group members
  const managers =
    group?.members?.filter(
      (member) => member.role === "super_user" || member.role === "site_admin",
    ) || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing && id) {
      const payload: UpdateGroupData = {
        name: formData.name,
        pin: formData.pin,
        userIds: formData.userIds,
        controllerUserPreset: formData.controllerUserPreset || null,
      };
      updateMutation.mutate({ id, data: payload });
    } else {
      if (!formData.siteId) {
        setUiAlert({
          open: true,
          title: "Validation",
          message: "Please select a site",
        });
        return;
      }
      const payload: CreateGroupData = {
        siteId: formData.siteId,
        name: formData.name,
        pin: formData.pin,
        userIds: formData.userIds,
        presetIds:
          formData.presetIds.length > 0 ? formData.presetIds : undefined,
        controllerUserPreset: formData.controllerUserPreset || undefined,
      };
      createMutation.mutate(payload);
    }
  };

  const selectedPresetIds = React.useMemo(() => {
    return isEditing
      ? (savedPresetIds ?? formData.presetIds)
      : formData.presetIds;
  }, [isEditing, savedPresetIds, formData.presetIds]);

  if (!hasPermission) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isEditing && groupLoading) {
    return <div className="p-6">Loading...</div>;
  }

  const isSubmitting =
    createMutation.isPending ||
    updateMutation.isPending ||
    savePresetMappingsMutation.isPending;

  return (
    <div className="px-2 py-3 sm:px-0">
      <div className="mb-6">
        <div className="items-start gap-4 w-full">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="flex items-center justify-between gap-3 w-full">
              <h1 className="text-2xl sm:text-3xl font-bold font-heading">
                {isEditing ? "Edit Group" : "Create Group"}
              </h1>
              <div className="hidden sm:flex items-center justify-end gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="group-detail-form"
                  disabled={isSubmitting}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSubmitting ? "Saving..." : isEditing ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form
        id="group-detail-form"
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div className="space-y-2">
          <Label>Site</Label>
          {currentSite ? (
            <div className="px-3 py-2 border rounded-md bg-muted/50">
              <p className="text-sm font-medium">{currentSite.name}</p>
            </div>
          ) : formData.siteId ? (
            <div className="px-3 py-2 border rounded-md bg-muted/50">
              <p className="text-sm font-medium text-muted-foreground">
                Loading site...
              </p>
            </div>
          ) : (
            <div className="px-3 py-2 border rounded-md bg-muted/50">
              <p className="text-sm font-medium text-muted-foreground">
                No site selected
              </p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pin">PIN *</Label>
          <Input
            id="pin"
            type="text"
            required
            value={formData.pin}
            onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
          />
        </div>

        {formData.siteId && allPresets && (
          <div className="space-y-2">
            <Label htmlFor="controllerUserPreset">Controller User Preset</Label>
            <Select
              value={formData.controllerUserPreset || "__none__"}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  controllerUserPreset: value === "__none__" ? "" : value,
                })
              }
            >
              <SelectTrigger id="controllerUserPreset">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {allPresets
                  .filter((preset) => preset.type === "user")
                  .map((preset) => {
                    const area = allAreas?.find((a) => a.id === preset.areaId);
                    return (
                      <SelectItem key={preset.id} value={preset.id}>
                        {area ? `${area.name} - ` : ""}
                        {preset.name}
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Optional: Reference to a preset of type 'user' from this site
            </p>
          </div>
        )}

        {isManagerGroup && isEditing && (
          <div className="space-y-2">
            <Label>Managers</Label>
            <p className="text-xs text-muted-foreground">
              Super users and site admins are automatically added to Manager
              groups. This section is read-only.
            </p>
            <Card className="p-4">
              {managers.length > 0 ? (
                <div className="space-y-2">
                  {managers.map((manager) => (
                    <div
                      key={manager.id}
                      className="flex items-start space-x-2 py-1 px-2 rounded"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">{manager.email}</p>
                        <p className="text-xs text-muted-foreground">
                          {manager.firstName || manager.lastName
                            ? `${manager.firstName || ""} ${
                                manager.lastName || ""
                              }`.trim()
                            : "No name"}{" "}
                          • {manager.role.replace("_", " ")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  No managers assigned to this group
                </p>
              )}
            </Card>
            {managers.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {managers.length} manager{managers.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Members (End Users Only)</Label>
            {isEditing && !isManagerGroup && (
              <Dialog
                open={addUserDialogOpen}
                onOpenChange={setAddUserDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add User to Group</DialogTitle>
                    <DialogDescription>
                      Search and select an end user to add to this group.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by email or name..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto border rounded-md">
                      {availableUsersForAdd.length > 0 ? (
                        <div className="divide-y">
                          {availableUsersForAdd.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                if (id) {
                                  addUserMutation.mutate({
                                    groupId: id,
                                    userId: user.id,
                                  });
                                }
                              }}
                              disabled={addUserMutation.isPending}
                              className="w-full text-left px-4 py-3 hover:bg-accent transition-colors disabled:opacity-50"
                            >
                              <p className="text-sm font-medium">
                                {user.email}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {user.firstName || user.lastName
                                  ? `${user.firstName || ""} ${
                                      user.lastName || ""
                                    }`.trim()
                                  : "No name"}
                              </p>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground p-4 text-center">
                          {userSearchQuery
                            ? "No users found matching your search"
                            : "No available users to add"}
                        </p>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {isManagerGroup && (
            <p className="text-xs text-muted-foreground">
              End users cannot be members of Manager groups.
            </p>
          )}
          <Card className="p-4">
            {isEditing && group?.members ? (
              // When editing, show current members from the group data
              group.members.filter((m) => m.role === "end_user").length > 0 ? (
                <div className="space-y-2">
                  {group.members
                    .filter((m) => m.role === "end_user")
                    .map((member) => (
                      <div
                        key={member.id}
                        className="flex items-start justify-between py-1 px-2 hover:bg-accent rounded"
                      >
                        <div className="flex-1">
                          <p className="text-sm">{member.email}</p>
                          <p className="text-xs text-muted-foreground">
                            {member.firstName || member.lastName
                              ? `${member.firstName || ""} ${
                                  member.lastName || ""
                                }`.trim()
                              : "No name"}{" "}
                            • {member.role.replace("_", " ")}
                          </p>
                        </div>
                        {!isManagerGroup && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (id) {
                                removeUserMutation.mutate({
                                  groupId: id,
                                  userId: member.id,
                                });
                              }
                            }}
                            disabled={removeUserMutation.isPending}
                            className="h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  No end users in this group
                </p>
              )
            ) : // When creating, show checkbox list
            siteUsers && siteUsers.length > 0 ? (
              <div className="space-y-2">
                {siteUsers.map((userOption) => (
                  <label
                    key={userOption.id}
                    className="flex items-start space-x-2 py-1 px-2 hover:bg-accent rounded cursor-pointer"
                  >
                    <Checkbox
                      checked={formData.userIds.includes(userOption.id)}
                      onCheckedChange={() => handleUserToggle(userOption.id)}
                      className="mt-0.5"
                      disabled={isManagerGroup}
                    />
                    <div className="flex-1">
                      <p className="text-sm">{userOption.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {userOption.firstName || userOption.lastName
                          ? `${userOption.firstName || ""} ${
                              userOption.lastName || ""
                            }`.trim()
                          : "No name"}{" "}
                        • {userOption.role.replace("_", " ")}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-2">
                {!formData.siteId && !isEditing
                  ? "Please select a site first"
                  : "No end users available for this site"}
              </p>
            )}
          </Card>
          {isEditing && group?.members ? (
            <p className="text-xs text-muted-foreground">
              {group.members.filter((m) => m.role === "end_user").length} member
              {group.members.filter((m) => m.role === "end_user").length !== 1
                ? "s"
                : ""}
            </p>
          ) : (
            formData.userIds.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {formData.userIds.length} member
                {formData.userIds.length !== 1 ? "s" : ""} selected
              </p>
            )
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label>Lighting Presets</Label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!formData.siteId || isSubmitting}
              onClick={() => setEditLightingPresetsOpen(true)}
            >
              Edit lighting presets
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Allocate this group to lighting area presets to allow users in this
            group to control the lighting in these areas.
          </p>
          <GroupLightingPresetsList
            variant="readonly"
            siteId={formData.siteId}
            lightPresets={lightPresets}
            presetsByArea={presetsByArea}
            groupIdForRelations={group?.id}
            siteCurfew={{
              start: currentSite?.curfewStart,
              end: currentSite?.curfewEnd,
            }}
            selectedPresetIds={selectedPresetIds}
            cardClassName="max-h-96 overflow-y-auto p-4"
            onRequestEdit={() => setEditLightingPresetsOpen(true)}
            onUiAlert={({ title, message }) =>
              setUiAlert({ open: true, title, message })
            }
            onOpenAvailabilityEditor={({
              preset,
              lightPresetGroupId,
            }) =>
              openLpgModalFromReadonlyList({
                preset,
                lightPresetGroupId,
              })
            }
          />
          {selectedPresetIds.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {selectedPresetIds.length} preset
              {selectedPresetIds.length !== 1 ? "s" : ""} selected
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 sm:hidden">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? "Saving..." : isEditing ? "Update" : "Create"}
          </Button>
        </div>
      </form>

      <Dialog
        open={editLightingPresetsOpen}
        onOpenChange={setEditLightingPresetsOpen}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit lighting presets</DialogTitle>
            <DialogDescription>
              Select which lighting presets this group can control. Availability
              is configured per selected preset ↔ group link.
            </DialogDescription>
          </DialogHeader>
          <GroupLightingPresetsList
            variant="edit"
            siteId={formData.siteId}
            lightPresets={lightPresets}
            presetsByArea={presetsByArea}
            draftPresetIds={draftPresetIds}
            groupIdForRelations={group?.id}
            siteCurfew={{
              start: currentSite?.curfewStart,
              end: currentSite?.curfewEnd,
            }}
            isEditingGroup={isEditing}
            isSubmitting={isSubmitting}
            disableEdits={savePresetMappingsMutation.isPending}
            onTogglePreset={handlePresetToggle}
            onToggleArea={handleAreaToggle}
            onOpenAvailabilityEditor={({
              preset,
              lightPresetGroupId,
            }) =>
              openLpgModalFromEditLightingPresets({
                preset,
                lightPresetGroupId,
              })
            }
            onUiAlert={({ title, message }) =>
              setUiAlert({ open: true, title, message })
            }
            onApplySelectionForCreate={() => {
              setFormData((p) => ({ ...p, presetIds: draftPresetIds }));
              setEditLightingPresetsOpen(false);
            }}
            onRequestClose={() => setEditLightingPresetsOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {lpgModal.open && lpgModal.lightPresetGroupId && lpgModal.preset && (
        <LightPresetGroupFormModal
          lightPresetGroupId={lpgModal.lightPresetGroupId}
          preset={lpgModal.preset}
          siteId={formData.siteId}
          onClose={closeLpgModal}
        />
      )}

      <AlertDialog
        open={uiAlert.open}
        onOpenChange={(open) => setUiAlert((p) => ({ ...p, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
            <AlertDialogDescription>{uiAlert.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
